
import React, { useState, useEffect } from 'react';
import { FormControl, InputLabel, Select, MenuItem } from '@mui/material';
import '../../assets/styles/dropdown.css';

// Function to group protocols by PROTOCOLNAME categories and sort categories by first date of each
const groupProtocolsByCategory = (protocols) => {
  const categories = {
    "Inactive Stim": [],
    "Dermatome Stim": [],
    "Frequency Stim": [],
    "Baseline Visit": []
  };

  // Group protocols by category
  protocols.forEach((protocol) => {
    const name = protocol.name;
    if (name.includes("Inactive")) {
      categories["Inactive Stim"].push(protocol);
    } else if (name.includes("Dermatome")) {
      categories["Dermatome Stim"].push(protocol);
    } else if (name.includes("Frequency")) {
      categories["Frequency Stim"].push(protocol);
    } else if (name.includes("Baseline")) {
      categories["Baseline Visit"].push(protocol);
    }
  });

  // Sort protocols within each category by date
  Object.keys(categories).forEach((category) => {
    categories[category].sort((a, b) => new Date(a.start_time) - new Date(b.start_time));
  });

  // Get the earliest date of each category and use it to sort the categories
  const sortedCategories = Object.entries(categories)
    .filter(([_, items]) => items.length > 0) // Remove empty categories
    .sort(([, protocolsA], [, protocolsB]) => {
      const firstDateA = new Date(protocolsA[0].start_time);
      const firstDateB = new Date(protocolsB[0].start_time);
      return firstDateA - firstDateB; // Sort by the first date within each category
    });

  return sortedCategories; // Returns an array of [category, protocols] sorted by earliest date
};

function ProtocolDropdown({ selectedPatient, onChange }) {
  const [protocols, setProtocols] = useState([]);
  const [selectedProtocol, setSelectedProtocol] = useState('');

  useEffect(() => {
    if (selectedPatient) {
      const fetchProtocols = async () => {
        try {
          console.log("Fetching protocols for patient:", selectedPatient);
          const response = await fetch('https://dashboard.encora-internal.com/api/get_protocols', {
            method: 'POST',
            credentials: 'include',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ selectedPatient })
          });
          const data = await response.json();
          console.log("Fetched protocols:", data);
          setProtocols(data);
        } catch (error) {
          console.error('Error fetching protocols:', error);
        }
      };
      fetchProtocols();
    }
  }, [selectedPatient]);

  const handleSelection = async (event) => {
    const protocolId = event.target.value;
    console.log("Handling protocol selection:", protocolId);
    setSelectedProtocol(protocolId);
    onChange(protocolId);

    try {
      const response = await fetch('https://dashboard.encora-internal.com/api/submit_protocol', {
        method: 'POST',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ selectedOption: protocolId })
      });

      if (!response.ok) {
        console.error("Failed to submit protocol", response.statusText);
      }
    } catch (error) {
      console.error("Error submitting protocol:", error);
    }
  };

  const groupedProtocols = groupProtocolsByCategory(protocols);

  // Create MenuItems grouped by category
  const menuItems = groupedProtocols.flatMap(([category, protocols]) => [
    <MenuItem key={`${category}-label`} disabled style={{ fontWeight: 'bold', color: '#000' }}>
      {category}
    </MenuItem>,
    ...protocols.map((protocol) => (
      <MenuItem key={protocol.id} value={protocol.id}>
        {"Protocol"} - {new Date(protocol.start_time).toLocaleString()}
      </MenuItem>
    )),
  ]);

  return (
    <FormControl fullWidth>
      <InputLabel>Protocol</InputLabel>
      <Select
        value={selectedProtocol || ''}
        onChange={handleSelection}
        displayEmpty
      >
        {menuItems}
      </Select>
    </FormControl>
  );
}

export default ProtocolDropdown;
